import React, { useCallback, useMemo, useState } from 'react';
import Style from './panelStyles.module.scss';
import { useNode } from '@craftjs/core';
import { ReactComponent as TextAlignLeft } from 'assets/img/generalBuilder/text_align_left_24.svg';
import { ReactComponent as TextAlignRight } from 'assets/img/generalBuilder/text_align_right_24.svg';
import { ReactComponent as TextAlignCenter } from 'assets/img/generalBuilder/text_align_center_24.svg';
import { ReactComponent as TextAlignJustify } from 'assets/img/generalBuilder/text_align_justify_24.svg';
import { PanelContainer } from './uiKit/panelContainer/panelContainer';
import { ControlsGroup } from './uiKit/controlsGroup/controlsGroup';
import { SettingsMenu } from './uiKit/menu/settingsMenu';
import { SettingsToggleButton, SettingsToggleButtonItem } from './uiKit/toggleButton/toggleButton';
import { hasSection, clamp, MIN_FONT_SIZE, MAX_FONT_SIZE, isValidTextSize, fontSizeArray } from './helpers/helper';
import { FormTextProps } from '../../types/Text';
import { Property } from 'csstype';
import { AppCheckboxInput, AppDropDownWithSuggestionInput } from 'uikit/inputs';
import { DropdownOption } from 'uikit/inputs/dropdown/appDropdownInput';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';
import { DropDownMenuOption } from 'uikit/dropDownMenu/dropDownMenu';

type TextPanelSection = 'Style' | 'Alignment';

export type TextPanelProps = {
  sections: TextPanelSection[];
};

export const TextPanel = ({ sections }: TextPanelProps) => {
  const { t } = useScopedTranslation('GeneralFormBuilder.Panels.Text');
  const {
    actions: { setCustom, setProp },
    styles,
    props,
  } = useNode((node) => ({
    styles: node.data.custom,
    props: node.data.props as FormTextProps,
  }));

  const [fontSizeIsCorrectNumber, setFontSizeIsCorrectNumber] = useState(true);

  //#region Options

  const textStyleOptions: DropDownMenuOption[] = useMemo(
    () => [
      { text: t('StyleOptions.Regular'), key: 'regular' },
      { text: t('StyleOptions.Bold'), key: 'bold' },
      { text: t('StyleOptions.Italic'), key: 'italic' },
    ],
    [t],
  );

  const textAlignmentOptions: SettingsToggleButtonItem<Property.TextAlign>[] = useMemo(
    () => [
      {
        icon: <TextAlignLeft />,
        value: 'left',
      },
      {
        icon: <TextAlignCenter />,
        value: 'center',
      },
      {
        icon: <TextAlignRight />,
        value: 'right',
      },
      {
        icon: <TextAlignJustify />,
        value: 'justify',
      },
    ],
    [],
  );

  //#endregion

  const changeStyleHandler = useCallback(
    (value: DropDownMenuOption) => {
      switch (value.key) {
        case 'bold':
          setCustom((prop: React.CSSProperties) => {
            prop.fontWeight = 600;
            prop.fontStyle = undefined;
          }, 500);
          return;

        case 'italic':
          setCustom((prop: React.CSSProperties) => {
            prop.fontWeight = undefined;
            prop.fontStyle = 'italic';
          }, 500);
          return;

        default:
          setCustom((prop: React.CSSProperties) => {
            prop.fontWeight = undefined;
            prop.fontStyle = undefined;
          }, 500);
          return;
      }
    },
    [setCustom],
  );

  const styleDefaultValue = useMemo(() => {
    if (styles.fontStyle && styles.fontStyle === 'italic') {
      return textStyleOptions.find((x) => x.key === 'italic') || textStyleOptions[0];
    }

    if (styles.fontWeight && styles.fontWeight > 400) {
      return textStyleOptions.find((x) => x.key === 'bold') || textStyleOptions[0];
    }

    return textStyleOptions[0];
  }, [styles.fontStyle, styles.fontWeight, textStyleOptions]);

  const changeFontSizeHandler = useCallback(
    (value: DropdownOption | undefined) => {
      setCustom((prop: React.CSSProperties) => {
        const isValidNumber = isValidTextSize(value?.text ?? null);
        setFontSizeIsCorrectNumber(isValidNumber);

        if (isValidNumber) {
          const parsedNumber = Number(value?.key);
          prop.fontSize = clamp(parsedNumber, MIN_FONT_SIZE, MAX_FONT_SIZE);
        }
      }, 500);
    },
    [setCustom],
  );

  const fontSizeOptions: DropdownOption[] = useMemo(
    () =>
      fontSizeArray.map(
        (x) =>
          ({
            key: x,
            text: x.toString(),
          } satisfies DropdownOption),
      ),
    [],
  );

  return (
    <PanelContainer header={t('Header')} panelName={'General'}>
      {hasSection(sections, 'Style') && (
        <ControlsGroup header={t('Style')} contentClassName={Style.gridContainer}>
          <SettingsMenu menuItems={textStyleOptions} defaultItem={styleDefaultValue} onSelect={changeStyleHandler} />
          <AppDropDownWithSuggestionInput
            suggestion
            isRequired
            hideCaret
            options={fontSizeOptions}
            value={fontSizeOptions.find((x) => x.key === styles.fontSize)}
            className={Style.fontSizeInput}
            invalid={!fontSizeIsCorrectNumber}
            onChange={changeFontSizeHandler}
          />
        </ControlsGroup>
      )}

      {hasSection(sections, 'Alignment') && (
        <ControlsGroup header={t('Alignment')} contentClassName={Style.flexContainer}>
          <SettingsToggleButton<Property.TextAlign>
            menuItems={textAlignmentOptions}
            defaultValue={styles.textAlign || textAlignmentOptions[0].value}
            onSelect={(value) =>
              setCustom((prop: React.CSSProperties) => {
                prop.textAlign = value;
              }, 500)
            }
          />
        </ControlsGroup>
      )}

      <ControlsGroup contentClassName={Style.flexContainer}>
        <AppCheckboxInput
          label={t('IsHidden')}
          checked={props.isHiddenOnOverview}
          onChange={(e) =>
            setProp((p: FormTextProps) => {
              p.isHiddenOnOverview = e.target.checked || undefined;
            }, 500)
          }
        />
      </ControlsGroup>
    </PanelContainer>
  );
};
